import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton
} from '@mui/material';
import { Add, Delete, Refresh } from '@mui/icons-material';

const VarleExcluded = () => {
  const [excludedProducts, setExcludedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newBarcode, setNewBarcode] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const apiBaseUrl = 'https://api.prixora.lt/api/ProductProcessing';

  const fetchExcludedProducts = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl}/excluded/varle`, {
        method: 'GET',
        headers: {
          'accept': '*/*'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExcludedProducts(data.products || []);
        setTotalCount(data.count || 0);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      setError('Nepavyko gauti išimtų produktų sąrašo: ' + err.message);
      setExcludedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const addExcludedProduct = async () => {
    if (!newBarcode.trim()) {
      return;
    }

    setAddLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl}/exclude`, {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          barcode: newBarcode.trim(),
          marketplace: 'varle'
        })
      });

      if (response.ok) {
        setNewBarcode('');
        setAddDialogOpen(false);
        // Refresh the list
        await fetchExcludedProducts();
      } else {
        const errorData = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorData}`);
      }
    } catch (err) {
      setError('Nepavyko pridėti produkto į išimčių sąrašą: ' + err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('lt-LT');
  };

  useEffect(() => {
    fetchExcludedProducts();
  }, []);

  const handleAddDialogOpen = () => {
    setAddDialogOpen(true);
    setNewBarcode('');
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
    setNewBarcode('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addExcludedProduct();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Varle Išimtos Prekės
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Produktai, kurie yra išimti iš Varle rinkos vietos
            </Typography>
            {totalCount > 0 && (
              <Chip
                label={`Iš viso: ${totalCount}`}
                color="primary"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchExcludedProducts}
              disabled={loading}
            >
              Atnaujinti
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddDialogOpen}
            >
              Pridėti Produktą
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Brūkšninis kodas</strong></TableCell>
                  <TableCell><strong>Atnaujinta</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {excludedProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography color="text.secondary">
                        Išimtų produktų nerasta
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  excludedProducts.map((product, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body1" fontFamily="monospace">
                          {product.barcode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(product.updatedAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add Product Dialog */}
      <Dialog open={addDialogOpen} onClose={handleAddDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Pridėti Produktą į Išimčių Sąrašą</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Brūkšninis kodas"
            type="text"
            fullWidth
            variant="outlined"
            value={newBarcode}
            onChange={(e) => setNewBarcode(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Įveskite brūkšninį kodą"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddDialogClose} disabled={addLoading}>
            Atšaukti
          </Button>
          <Button
            onClick={addExcludedProduct}
            variant="contained"
            disabled={addLoading || !newBarcode.trim()}
          >
            {addLoading ? <CircularProgress size={24} /> : 'Pridėti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VarleExcluded;